
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {OrderChange} from './OrderChange';
import {FieldModelOrderChange} from './OrderChange';
import {MarginChange} from './MarginChange';
import {FieldModelMarginChange} from './MarginChange';
import {TradeHistoryRecord} from '../entity/TradeHistoryRecord';
import {FieldModelTradeHistoryRecord} from '../entity/TradeHistoryRecord';
import {TradeEvent} from '../entity/TradeEvent';
import {FieldModelTradeEvent} from '../entity/TradeEvent';

/**
 * OneOfEntityChange struct
 */
class OneOfEntityChange {
  /**
   * Initialize struct
   * @param {OrderChange=} order
   * @param {MarginChange=} margin
   * @param {TradeHistoryRecord=} history
   * @param {!Array=} events
   * @constructor
   */
  constructor (order = null, margin = null, history = null, events = []) {
    this.Order = order
    this.Margin = margin
    this.History = history
    this.Events = events
  }

  /**
   * Copy struct (shallow copy)
   * @this {!OneOfEntityChange}
   * @param {!OneOfEntityChange} other Other struct
   * @returns {!OneOfEntityChange} This struct
   */
  copy (other) {
    if (other.Order != null) {
      this.Order = OrderChange.fromObject(other.Order)
    } else {
      this.Order = null
    }
    if (other.Margin != null) {
      this.Margin = MarginChange.fromObject(other.Margin)
    } else {
      this.Margin = null
    }
    if (other.History != null) {
      this.History = TradeHistoryRecord.fromObject(other.History)
    } else {
      this.History = null
    }
    if (other.Events != null) {
      this.Events = []
      for (let item of other.Events) {
        if (item != null) {
          let tempItem
          tempItem = TradeEvent.fromObject(item)
          this.Events.push(tempItem)
        } else {
          this.Events.push(null)
        }
      }
    } else {
      this.Events = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!OneOfEntityChange}
   * @returns {!OneOfEntityChange} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new OneOfEntityChangeModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new OneOfEntityChangeModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!OneOfEntityChange}
   * @param {!OneOfEntityChange} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof OneOfEntityChange)) {
      throw new TypeError('Instance of OneOfEntityChange is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!OneOfEntityChange}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Order: ((this.Order != null) ? this.Order : null),
      Margin: ((this.Margin != null) ? this.Margin : null),
      History: ((this.History != null) ? this.History : null),
      Events: ((this.Events != null) ? Array.from(this.Events, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return OneOfEntityChange.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!OneOfEntityChange} other Object value
   * @returns {!OneOfEntityChange} Created struct
   */
  static fromObject (other) {
    return new OneOfEntityChange().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!OneOfEntityChange}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return OneOfEntityChange.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!OneOfEntityChange}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4202
  }
}

export { OneOfEntityChange };

/**
 * Fast Binary Encoding OneOfEntityChange field model
 */
class FieldModelOneOfEntityChange extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Order = new fbe.FieldModelOptional(new FieldModelOrderChange(buffer, 4 + 4), buffer, 4 + 4)
    this.Margin = new fbe.FieldModelOptional(new FieldModelMarginChange(buffer, this.Order.fbeOffset + this.Order.fbeSize), buffer, this.Order.fbeOffset + this.Order.fbeSize)
    this.History = new fbe.FieldModelOptional(new FieldModelTradeHistoryRecord(buffer, this.Margin.fbeOffset + this.Margin.fbeSize), buffer, this.Margin.fbeOffset + this.Margin.fbeSize)
    this.Events = new fbe.FieldModelVector(new FieldModelTradeEvent(buffer, this.History.fbeOffset + this.History.fbeSize), buffer, this.History.fbeOffset + this.History.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Order.fbeSize + this.Margin.fbeSize + this.History.fbeSize + this.Events.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Order.fbeExtra + this.Margin.fbeExtra + this.History.fbeExtra + this.Events.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelOneOfEntityChange.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4202
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelOneOfEntityChange}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelOneOfEntityChange}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Order.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Margin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.History.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.History.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.History.fbeSize

    if ((fbeCurrentSize + this.Events.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Events.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Events.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelOneOfEntityChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelOneOfEntityChange}
   * @param {!OneOfEntityChange} fbeValue Default value, defaults is new OneOfEntityChange()
   * @returns {!OneOfEntityChange} OneOfEntityChange value
   */
  get (fbeValue = new OneOfEntityChange()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelOneOfEntityChange}
   * @param {!OneOfEntityChange} fbeValue OneOfEntityChange value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) <= fbeStructSize) {
      fbeValue.Order = this.Order.get()
    } else {
      fbeValue.Order = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) <= fbeStructSize) {
      fbeValue.Margin = this.Margin.get()
    } else {
      fbeValue.Margin = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.History.fbeSize) <= fbeStructSize) {
      fbeValue.History = this.History.get()
    } else {
      fbeValue.History = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.History.fbeSize

    if ((fbeCurrentSize + this.Events.fbeSize) <= fbeStructSize) {
      this.Events.get(fbeValue.Events)
    } else {
      fbeValue.Events.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Events.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelOneOfEntityChange}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelOneOfEntityChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelOneOfEntityChange}
   * @param {!OneOfEntityChange} fbeValue OneOfEntityChange value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelOneOfEntityChange}
   * @param {OneOfEntityChange} fbeValue OneOfEntityChange value
   */
  setFields (fbeValue) {
    this.Order.set(fbeValue.Order)
    this.Margin.set(fbeValue.Margin)
    this.History.set(fbeValue.History)
    this.Events.set(fbeValue.Events)
  }
}

export { FieldModelOneOfEntityChange };

/**
 * Fast Binary Encoding OneOfEntityChange model
 */
class OneOfEntityChangeModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelOneOfEntityChange(this.buffer, 4)
  }

  /**
   * Get the OneOfEntityChange model
   * @this {!OneOfEntityChangeModel}
   * @returns {!FieldModelOneOfEntityChange} model OneOfEntityChange model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!OneOfEntityChangeModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!OneOfEntityChangeModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return OneOfEntityChangeModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!OneOfEntityChangeModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelOneOfEntityChange.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!OneOfEntityChangeModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!OneOfEntityChangeModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!OneOfEntityChangeModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!OneOfEntityChangeModel}
   * @param {!OneOfEntityChange} value OneOfEntityChange value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!OneOfEntityChangeModel}
   * @param {!OneOfEntityChange} value OneOfEntityChange value, defaults is new OneOfEntityChange()
   * @return {!object} Deserialized OneOfEntityChange value and its size
   */
  deserialize (value = new OneOfEntityChange()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new OneOfEntityChange(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new OneOfEntityChange(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!OneOfEntityChangeModel}
   * @param {!number} prev Previous OneOfEntityChange model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { OneOfEntityChangeModel };
